import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Sparkles, Target, Orbit, Maximize2, Minimize2, Flame } from "lucide-react";
import lerniCatAstronaut from "@assets/lerni-cat-astronaut.png";
import { API_BASE } from "@/lib/api";

const NEBULA_SIZE = 10;
const PLANETS_PER_NEBULA = 7;

interface StarData {
  id: number;
  sessionId: number;
  sessionTitle: string;
  score: number;
  unlockedAt: string;
}

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  planets: number;
}

interface NebulaCluster {
  index: number;
  centerX: number;
  centerY: number;
  stars: PlacedStar[];
  isComplete: boolean;
  planets: PlacedPlanet[];
}

interface PlacedStar {
  id: number;
  sessionId: number;
  sessionTitle: string;
  score: number;
  unlockedAt: string;
  x: number;
  y: number;
  brightness: number;
  size: number;
  pulseSpeed: number;
  pulseOffset: number;
  hue: number;
  nebulaIndex: number;
}

interface PlacedPlanet {
  index: number;
  x: number;
  y: number;
  variant: number;
  size: number;
  orbitAngle: number;
  floatOffset: number;
  nebulaIndex: number;
}

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
}

function apiCall(path: string) {
  const token = localStorage.getItem("studyflow_token");
  return fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(async (r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });
}

const NEBULA_SPACING = 600;

function buildNebulae(stars: StarData[], planetCount: number): NebulaCluster[] {
  const nebulaCount = Math.max(1, Math.ceil(stars.length / NEBULA_SIZE));
  const nebulae: NebulaCluster[] = [];

  for (let n = 0; n < nebulaCount; n++) {
    const rng = seededRandom(n * 9973 + 1);
    const angle = n * 2.399963 * 3;
    const dist = n === 0 ? 0 : NEBULA_SPACING * Math.sqrt(n) + rng() * 80;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist;

    const startIdx = n * NEBULA_SIZE;
    const endIdx = Math.min(startIdx + NEBULA_SIZE, stars.length);
    const clusterStars = stars.slice(startIdx, endIdx);
    const isComplete = clusterStars.length >= NEBULA_SIZE;

    const placedStars: PlacedStar[] = clusterStars.map((star, i) => {
      const srng = seededRandom(star.sessionId * 7919 + star.id * 31);
      const goldenAngle = 2.399963;
      const a = i * goldenAngle;
      const r = 30 + Math.sqrt((i + 1) / NEBULA_SIZE) * 130 + srng() * 25;
      return {
        ...star,
        x: cx + Math.cos(a) * r,
        y: cy + Math.sin(a) * r,
        brightness: 0.75 + srng() * 0.25,
        size: 2.5 + srng() * 1.5,
        pulseSpeed: 0.001 + srng() * 0.002,
        pulseOffset: srng() * Math.PI * 2,
        hue: 250 + srng() * 50,
        nebulaIndex: n,
      };
    });

    nebulae.push({
      index: n,
      centerX: cx,
      centerY: cy,
      stars: placedStars,
      isComplete,
      planets: [],
    });
  }

  let planetsRemaining = planetCount;
  for (let n = 0; n < nebulae.length && planetsRemaining > 0; n++) {
    const toPlace = Math.min(planetsRemaining, PLANETS_PER_NEBULA);
    const neb = nebulae[n];
    const prng = seededRandom(n * 4217 + 7);
    const orbitRadius = 200 + prng() * 30;

    for (let p = 0; p < toPlace; p++) {
      const angle = (p / toPlace) * Math.PI * 2 + prng() * 0.3;
      neb.planets.push({
        index: p + n * PLANETS_PER_NEBULA,
        x: neb.centerX + Math.cos(angle) * orbitRadius,
        y: neb.centerY + Math.sin(angle) * orbitRadius,
        variant: Math.floor(prng() * 4),
        size: 8 + prng() * 5,
        orbitAngle: angle,
        floatOffset: prng() * Math.PI * 2,
        nebulaIndex: n,
      });
    }
    planetsRemaining -= toPlace;
  }

  return nebulae;
}

interface BgStar {
  x: number;
  y: number;
  r: number;
  b: number;
  layer: number;
}

function generateBgStars(count: number, seed: number): BgStar[] {
  const rng = seededRandom(seed);
  const result: BgStar[] = [];
  for (let i = 0; i < count; i++) {
    result.push({
      x: rng() * 6000 - 3000,
      y: rng() * 6000 - 3000,
      r: rng() * 1.0 + 0.2,
      b: rng() * 0.4 + 0.15,
      layer: rng() < 0.3 ? 0 : rng() < 0.7 ? 1 : 2,
    });
  }
  return result;
}

const PARALLAX_FACTORS = [0.3, 0.6, 1.0];

type TooltipData = {
  x: number;
  y: number;
  kind: "star";
  title: string;
  score: number;
  date: string;
} | {
  x: number;
  y: number;
  kind: "planet";
  label: string;
};

function drawPlanet(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  size: number,
  variant: number,
  time: number,
  floatOffset: number,
  hovered: boolean
) {
  const floatY = Math.sin(time * 0.0006 + floatOffset) * 2;
  const fy = py + floatY;
  const glowAlpha = hovered ? 0.35 : 0.12;
  const glowSize = hovered ? size * 3.5 : size * 2.5;

  const baseHues = [220, 280, 35, 160];
  const hue = baseHues[variant % 4];

  const outerGlow = ctx.createRadialGradient(px, fy, size * 0.5, px, fy, glowSize);
  outerGlow.addColorStop(0, `hsla(${hue}, 60%, 60%, ${glowAlpha})`);
  outerGlow.addColorStop(0.5, `hsla(${hue}, 50%, 40%, ${glowAlpha * 0.3})`);
  outerGlow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = outerGlow;
  ctx.beginPath();
  ctx.arc(px, fy, glowSize, 0, Math.PI * 2);
  ctx.fill();

  if (variant === 0) {
    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.strokeStyle = `hsla(${hue}, 40%, 70%, 0.5)`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(px, fy, size * 2, size * 0.5, -0.3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  const bodyGrad = ctx.createRadialGradient(
    px - size * 0.3, fy - size * 0.3, size * 0.1,
    px, fy, size
  );

  if (variant === 0) {
    bodyGrad.addColorStop(0, `hsla(${hue}, 50%, 75%, 1)`);
    bodyGrad.addColorStop(0.6, `hsla(${hue}, 45%, 50%, 1)`);
    bodyGrad.addColorStop(1, `hsla(${hue}, 40%, 25%, 1)`);
  } else if (variant === 1) {
    bodyGrad.addColorStop(0, `hsla(${hue}, 55%, 70%, 1)`);
    bodyGrad.addColorStop(0.5, `hsla(${hue}, 50%, 45%, 1)`);
    bodyGrad.addColorStop(1, `hsla(${hue}, 45%, 20%, 1)`);
  } else if (variant === 2) {
    bodyGrad.addColorStop(0, `hsla(${hue}, 60%, 80%, 1)`);
    bodyGrad.addColorStop(0.4, `hsla(${hue}, 55%, 55%, 1)`);
    bodyGrad.addColorStop(1, `hsla(${hue}, 50%, 30%, 1)`);
  } else {
    bodyGrad.addColorStop(0, `hsla(${hue}, 30%, 45%, 1)`);
    bodyGrad.addColorStop(0.5, `hsla(${hue}, 35%, 25%, 1)`);
    bodyGrad.addColorStop(1, `hsla(${hue}, 40%, 12%, 1)`);
  }

  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  ctx.arc(px, fy, size, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.globalAlpha = 0.25;
  const rimGrad = ctx.createRadialGradient(
    px + size * 0.4, fy - size * 0.3, 0,
    px, fy, size
  );
  rimGrad.addColorStop(0, `hsla(${hue}, 60%, 90%, 0.6)`);
  rimGrad.addColorStop(0.5, "rgba(255,255,255,0)");
  rimGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = rimGrad;
  ctx.beginPath();
  ctx.arc(px, fy, size, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  if (variant === 0) {
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = `hsla(${hue}, 40%, 70%, 0.6)`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(px, fy, size * 2, size * 0.5, -0.3, Math.PI, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

function CosmicCanvas({
  nebulae,
  isFullscreen,
}: {
  nebulae: NebulaCluster[];
  isFullscreen: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const cameraRef = useRef({ x: 0, y: 0 });
  const targetCameraRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    camX: number;
    camY: number;
  } | null>(null);

  const bgStarsRef = useRef<BgStar[]>(generateBgStars(600, 42));

  const allStars = nebulae.flatMap((n) => n.stars);
  const allPlanets = nebulae.flatMap((n) => n.planets);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      camX: targetCameraRef.current.x,
      camY: targetCameraRef.current.y,
    };
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        targetCameraRef.current.x = dragRef.current.camX + dx;
        targetCameraRef.current.y = dragRef.current.camY + dy;
        setTooltip(null);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const w = rect.width;
      const h = rect.height;
      const cam = cameraRef.current;
      const scale = scaleRef.current;
      const cx = w / 2 + cam.x;
      const cy = h / 2 + cam.y;

      for (const p of allPlanets) {
        const px = cx + p.x * scale;
        const py = cy + p.y * scale;
        const dist = Math.sqrt((mx - px) ** 2 + (my - py) ** 2);
        if (dist < p.size * scale * 2) {
          setTooltip({ x: mx, y: my, kind: "planet", label: "7-day streak milestone" });
          return;
        }
      }

      for (const s of allStars) {
        const sx = cx + s.x * scale;
        const sy = cy + s.y * scale;
        const dist = Math.sqrt((mx - sx) ** 2 + (my - sy) ** 2);
        if (dist < Math.max(s.size * scale * 3.5, 14)) {
          setTooltip({
            x: mx,
            y: my,
            kind: "star",
            title: s.sessionTitle,
            score: Math.round(s.score),
            date: new Date(s.unlockedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
          });
          return;
        }
      }

      setTooltip(null);
    },
    [allStars, allPlanets]
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 0.93 : 1.07;
    scaleRef.current = Math.max(0.2, Math.min(4, scaleRef.current * delta));
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? 0.93 : 1.07;
      scaleRef.current = Math.max(0.2, Math.min(4, scaleRef.current * delta));
    };
    container.addEventListener("wheel", handler, { passive: false });
    return () => container.removeEventListener("wheel", handler);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bgStars = bgStarsRef.current;
    let hoveredPlanetIdx = -1;

    const draw = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      const wPx = Math.round(w * dpr);
      const hPx = Math.round(h * dpr);
      if (canvas.width !== wPx || canvas.height !== hPx) {
        canvas.width = wPx;
        canvas.height = hPx;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cam = cameraRef.current;
      const target = targetCameraRef.current;
      cam.x += (target.x - cam.x) * 0.1;
      cam.y += (target.y - cam.y) * 0.1;
      const scale = scaleRef.current;

      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, "#020617");
      grad.addColorStop(0.5, "#030712");
      grad.addColorStop(1, "#050816");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      for (const bg of bgStars) {
        const pf = PARALLAX_FACTORS[bg.layer];
        const bx = w / 2 + (bg.x + cam.x * pf) * 0.5;
        const by = h / 2 + (bg.y + cam.y * pf) * 0.5;
        if (bx < -10 || bx > w + 10 || by < -10 || by > h + 10) continue;
        const flicker = bg.b + Math.sin(time * 0.0008 + bg.x * 0.1 + bg.y * 0.07) * 0.12;
        ctx.globalAlpha = Math.max(0, Math.min(1, flicker));
        ctx.fillStyle = bg.layer === 0 ? "#8888aa" : bg.layer === 1 ? "#aaaacc" : "#ccccee";
        ctx.beginPath();
        ctx.arc(bx, by, bg.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const viewCx = w / 2 + cam.x;
      const viewCy = h / 2 + cam.y;

      for (const neb of nebulae) {
        const nebCx = viewCx + neb.centerX * scale;
        const nebCy = viewCy + neb.centerY * scale;

        if (nebCx < -400 || nebCx > w + 400 || nebCy < -400 || nebCy > h + 400) continue;

        const isProto = !neb.isComplete && neb.stars.length > 0;
        const hasGlow = neb.isComplete || isProto;

        if (hasGlow) {
          const nebRadius = (neb.isComplete ? 180 : 100) * scale;
          const pulse = Math.sin(time * 0.0003 + neb.index * 1.5) * 0.06 + 1;
          const baseAlpha = neb.isComplete ? 1 : 0.4;

          const layers = [
            { ox: 0, oy: 0, r: nebRadius * pulse * 1.6, colors: [
              `rgba(100, 60, 200, ${0.15 * baseAlpha})`,
              `rgba(70, 50, 160, ${0.08 * baseAlpha})`,
              `rgba(50, 80, 180, ${0.03 * baseAlpha})`,
              "rgba(0,0,0,0)"
            ]},
            { ox: 35 * scale, oy: -25 * scale, r: nebRadius * pulse * 1.3, colors: [
              `rgba(130, 50, 170, ${0.12 * baseAlpha})`,
              `rgba(100, 40, 150, ${0.05 * baseAlpha})`,
              "rgba(0,0,0,0)"
            ]},
            { ox: -30 * scale, oy: 30 * scale, r: nebRadius * pulse * 1.1, colors: [
              `rgba(50, 100, 210, ${0.10 * baseAlpha})`,
              `rgba(35, 70, 160, ${0.04 * baseAlpha})`,
              "rgba(0,0,0,0)"
            ]},
          ];

          for (const layer of layers) {
            const lx = nebCx + layer.ox;
            const ly = nebCy + layer.oy;
            const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, layer.r);
            const step = 1 / (layer.colors.length - 1);
            layer.colors.forEach((c, ci) => g.addColorStop(ci * step, c));
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(lx, ly, layer.r, 0, Math.PI * 2);
            ctx.fill();
          }

          if (neb.isComplete) {
            for (const s of neb.stars) {
              const sx = viewCx + s.x * scale;
              const sy = viewCy + s.y * scale;
              const connAlpha = 0.025 + Math.sin(time * 0.0005 + s.pulseOffset) * 0.012;
              ctx.strokeStyle = `rgba(160, 130, 255, ${connAlpha})`;
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(sx, sy);
              ctx.lineTo(nebCx, nebCy);
              ctx.stroke();
            }
          }
        }

        for (const s of neb.stars) {
          const sx = viewCx + s.x * scale;
          const sy = viewCy + s.y * scale;
          if (sx < -50 || sx > w + 50 || sy < -50 || sy > h + 50) continue;

          const pulse = Math.sin(time * s.pulseSpeed + s.pulseOffset) * 0.25 + 1;
          const r = s.size * scale * pulse;
          const glowR = 14 * scale * pulse;

          const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, glowR);
          glow.addColorStop(0, `hsla(${s.hue}, 70%, 85%, ${0.35 * s.brightness})`);
          glow.addColorStop(0.4, `hsla(${s.hue}, 60%, 65%, ${0.12 * s.brightness})`);
          glow.addColorStop(1, "rgba(0, 0, 0, 0)");
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(sx, sy, glowR, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = `rgba(255, 255, 255, ${s.brightness * 0.95})`;
          ctx.beginPath();
          ctx.arc(sx, sy, r, 0, Math.PI * 2);
          ctx.fill();

          if (r > 1.5) {
            ctx.globalAlpha = s.brightness * 0.3;
            ctx.fillStyle = "#fff";
            ctx.fillRect(sx - r * 2.5, sy - 0.4, r * 5, 0.8);
            ctx.fillRect(sx - 0.4, sy - r * 2.5, 0.8, r * 5);
            ctx.globalAlpha = 1;
          }
        }

        for (const p of neb.planets) {
          const px = viewCx + p.x * scale;
          const py = viewCy + p.y * scale;
          if (px < -50 || px > w + 50 || py < -50 || py > h + 50) continue;
          const isHovered = p.index === hoveredPlanetIdx;
          drawPlanet(ctx, px, py, p.size * scale, p.variant, time, p.floatOffset, isHovered);
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [nebulae]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        handleMouseUp();
        setTooltip(null);
      }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />

      {tooltip && (
        <div
          className="absolute z-10 pointer-events-none px-3.5 py-2.5 rounded-xl border shadow-2xl"
          style={{
            left: Math.min(tooltip.x + 18, (containerRef.current?.clientWidth || 400) - 200),
            top: tooltip.y - 12,
            maxWidth: 240,
            background: "rgba(10, 12, 30, 0.92)",
            borderColor: tooltip.kind === "planet" ? "rgba(100, 200, 255, 0.2)" : "rgba(140, 120, 255, 0.2)",
            backdropFilter: "blur(12px)",
          }}
        >
          {tooltip.kind === "star" ? (
            <>
              <p className="text-sm font-semibold text-white/90 truncate">{tooltip.title}</p>
              <p className="text-xs mt-1" style={{ color: "rgba(180, 160, 255, 0.7)" }}>
                Score: {tooltip.score}% · {tooltip.date}
              </p>
            </>
          ) : (
            <p className="text-sm font-medium" style={{ color: "rgba(160, 210, 255, 0.9)" }}>
              {tooltip.label}
            </p>
          )}
        </div>
      )}

      {allStars.length > 0 && (
        <div
          className="absolute bottom-4 right-4 px-3.5 py-2 rounded-xl text-xs font-medium border"
          style={{
            background: "rgba(10, 12, 30, 0.75)",
            borderColor: "rgba(140, 120, 255, 0.15)",
            color: "rgba(200, 190, 255, 0.7)",
            backdropFilter: "blur(8px)",
          }}
        >
          {allStars.length} star{allStars.length !== 1 ? "s" : ""}
          {" · "}
          {nebulae.filter((n) => n.isComplete).length} nebula{nebulae.filter((n) => n.isComplete).length !== 1 ? "e" : ""}
          {allPlanets.length > 0 && (
            <span style={{ color: "rgba(160, 210, 255, 0.9)" }}>
              {" · "}{allPlanets.length} planet{allPlanets.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FloatingCat({ containerRef }: { containerRef: React.RefObject<HTMLDivElement | null> }) {
  const catRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0, y: 0 });
  const velRef = useRef({ x: 0, y: 0 });
  const driftRef = useRef({ x: 0.15, y: 0.08 });
  const stateRef = useRef<"idle" | "dragging" | "thrown">("idle");
  const dragRef = useRef({ lastX: 0, lastY: 0, lastTime: 0 });
  const animRef = useRef<number>(0);
  const CAT_SIZE = 100;
  const THROW_MULT = 0.58;
  const MAX_THROW_VEL = 9;
  const FRICTION = 0.96;
  const IDLE_THRESHOLD = 0.15;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    posRef.current = { x: rect.width * 0.8, y: rect.height * 0.3 };
    const angle = Math.random() * Math.PI * 2;
    driftRef.current = { x: Math.cos(angle) * 0.12, y: Math.sin(angle) * 0.08 };
    if (catRef.current) {
      catRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`;
    }
  }, [containerRef]);

  useEffect(() => {
    const animate = () => {
      const container = containerRef.current;
      const cat = catRef.current;
      if (!container || !cat) {
        animRef.current = requestAnimationFrame(animate);
        return;
      }

      if (stateRef.current === "dragging") {
        const floatRot = Math.sin(Date.now() / 800) * 5;
        cat.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px) rotate(${floatRot}deg)`;
        animRef.current = requestAnimationFrame(animate);
        return;
      }

      const rect = container.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      const pos = posRef.current;
      const vel = velRef.current;
      const drift = driftRef.current;

      const halfCat = CAT_SIZE / 2;

      if (stateRef.current === "thrown") {
        pos.x += vel.x;
        pos.y += vel.y;
        vel.x *= FRICTION;
        vel.y *= FRICTION;
        if (Math.abs(vel.x) < IDLE_THRESHOLD && Math.abs(vel.y) < IDLE_THRESHOLD) {
          vel.x = 0;
          vel.y = 0;
          stateRef.current = "idle";
        }
      } else {
        pos.x += drift.x;
        pos.y += drift.y;
        drift.x += (Math.random() - 0.5) * 0.003;
        drift.y += (Math.random() - 0.5) * 0.003;
        const driftSpeed = Math.sqrt(drift.x * drift.x + drift.y * drift.y);
        if (driftSpeed > 0.2) {
          drift.x *= 0.2 / driftSpeed;
          drift.y *= 0.2 / driftSpeed;
        }
        if (driftSpeed < 0.05) {
          drift.x += (Math.random() - 0.5) * 0.05;
          drift.y += (Math.random() - 0.5) * 0.05;
        }
      }

      if (pos.x > W) pos.x = -CAT_SIZE;
      else if (pos.x < -CAT_SIZE) pos.x = W;
      if (pos.y > H) pos.y = -CAT_SIZE;
      else if (pos.y < -CAT_SIZE) pos.y = H;

      const t = Date.now();
      const floatY = Math.sin(t / 1400) * 5 + Math.sin(t / 2200) * 3;
      const floatRot = Math.sin(t / 1800) * 2.5 + Math.cos(t / 2600) * 1.5;

      cat.style.transform = `translate3d(${pos.x}px, ${pos.y + floatY}px, 0) rotate(${floatRot}deg)`;

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [containerRef]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    stateRef.current = "dragging";
    dragRef.current = { lastX: e.clientX, lastY: e.clientY, lastTime: Date.now() };
    velRef.current = { x: 0, y: 0 };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (stateRef.current !== "dragging") return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    posRef.current.x += dx;
    posRef.current.y += dy;
    const now = Date.now();
    const dt = Math.max(now - dragRef.current.lastTime, 1);
    velRef.current = {
      x: Math.max(-MAX_THROW_VEL, Math.min(MAX_THROW_VEL, (dx / dt) * 16 * THROW_MULT)),
      y: Math.max(-MAX_THROW_VEL, Math.min(MAX_THROW_VEL, (dy / dt) * 16 * THROW_MULT)),
    };
    dragRef.current = { lastX: e.clientX, lastY: e.clientY, lastTime: now };
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const speed = Math.sqrt(velRef.current.x ** 2 + velRef.current.y ** 2);
    stateRef.current = speed > IDLE_THRESHOLD ? "thrown" : "idle";
  }, []);

  const stopMouse = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      ref={catRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseDown={stopMouse}
      onMouseMove={stopMouse}
      onMouseUp={stopMouse}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: CAT_SIZE,
        height: CAT_SIZE,
        cursor: "grab",
        zIndex: 50,
        touchAction: "none",
        userSelect: "none",
        filter: "drop-shadow(0 0 12px rgba(167, 139, 250, 0.4))",
      }}
    >
      <img
        src={lerniCatAstronaut}
        alt="Lerni the astronaut cat"
        style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        draggable={false}
      />
    </div>
  );
}

export default function Space() {
  const [stars, setStars] = useState<StarData[]>([]);
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([apiCall("/api/space/stars"), apiCall("/api/space/streaks")])
      .then(([starsData, streaks]) => {
        setStars(starsData);
        setStreakData(streaks);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isFullscreen]);

  const nebulae = buildNebulae(stars, streakData?.planets || 0);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: "#030712" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#8b7cff" }} />
      </div>
    );
  }

  const content = (
    <div
      className={isFullscreen ? "fixed inset-0 z-50 flex flex-col" : "h-full flex flex-col"}
      style={{
        background: "linear-gradient(180deg, #020617 0%, #030712 50%, #050816 100%)",
        color: "#e2e0f0",
      }}
    >
      <div className="shrink-0 px-6 pt-5 pb-3 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#ede9ff" }}>
            Your Nebula
          </h1>
          <p className="text-sm mt-1" style={{ color: "rgba(180, 175, 210, 0.65)" }}>
            Your personal constellation of mastered knowledge.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {streakData && streakData.currentStreak > 0 && (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{
                background: "rgba(255, 160, 50, 0.1)",
                border: "1px solid rgba(255, 160, 50, 0.2)",
                color: "rgba(255, 190, 100, 0.9)",
              }}
            >
              <Flame className="w-3.5 h-3.5" />
              {streakData.currentStreak} day streak
            </div>
          )}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 rounded-lg transition-colors"
            style={{
              background: "rgba(100, 80, 200, 0.1)",
              border: "1px solid rgba(100, 80, 200, 0.15)",
              color: "rgba(200, 190, 255, 0.7)",
            }}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {!isFullscreen && (
        <div className="shrink-0 px-6 pb-4 flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(200, 190, 255, 0.6)" }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: "#a78bfa" }} />
            <span>Each star = a mastered lecture</span>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(200, 190, 255, 0.6)" }}>
            <Target className="w-3.5 h-3.5" style={{ color: "#60a5fa" }} />
            <span>Score 80%+ on MCQs to unlock</span>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(200, 190, 255, 0.6)" }}>
            <Orbit className="w-3.5 h-3.5" style={{ color: "#c084fc" }} />
            <span>{NEBULA_SIZE} stars form a Nebula</span>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(200, 190, 255, 0.6)" }}>
            <Flame className="w-3.5 h-3.5" style={{ color: "#fb923c" }} />
            <span>7-day streaks earn planets</span>
          </div>
        </div>
      )}

      <div className={isFullscreen ? "flex-1 min-h-0" : "flex-1 min-h-0 px-4 pb-4"}>
        <div
          ref={canvasContainerRef}
          className={isFullscreen ? "w-full h-full relative" : "w-full h-full rounded-2xl overflow-hidden border relative"}
          style={isFullscreen ? {} : {
            borderColor: "rgba(100, 80, 200, 0.12)",
            boxShadow: "0 0 80px rgba(100, 60, 200, 0.06), inset 0 1px 0 rgba(255,255,255,0.03)",
          }}
        >
          <FloatingCat containerRef={canvasContainerRef} />
          {stars.length === 0 ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center px-6"
              style={{ background: "#030712" }}
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{
                  background: "rgba(120, 80, 220, 0.08)",
                  border: "1px solid rgba(120, 80, 220, 0.15)",
                  boxShadow: "0 0 40px rgba(120, 80, 220, 0.08)",
                }}
              >
                <Sparkles className="w-9 h-9" style={{ color: "rgba(160, 140, 255, 0.35)" }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: "rgba(230, 225, 255, 0.8)" }}>
                Your space is still dark
              </h3>
              <p className="max-w-sm text-sm leading-relaxed" style={{ color: "rgba(180, 175, 210, 0.45)" }}>
                Complete a lecture quiz with 80% or higher to light your first star.
                Each star you earn brings your Nebula closer to life.
              </p>
            </div>
          ) : (
            <CosmicCanvas nebulae={nebulae} isFullscreen={isFullscreen} />
          )}
        </div>
      </div>
    </div>
  );

  return content;
}
